import { TeamData, TeamType } from "@/types/user";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { teamsService } from "@/services/teamsService";

type TeamSelectionStepProps = {
  selectedTeam: TeamType;
  setSelectedTeam: (team: TeamType) => void;
  selectedTeamId: string;
  setSelectedTeamId: (id: string) => void;
  selectedTeamName: string;
  setSelectedTeamName: (name: string) => void;
  organizationId?: string;
};

const TeamSelectionStep = ({
  selectedTeam,
  setSelectedTeam,
  selectedTeamId,
  setSelectedTeamId,
  selectedTeamName,
  setSelectedTeamName,
  organizationId,
}: TeamSelectionStepProps) => {
  const [teams, setTeams] = useState<TeamData[]>([]);
  const [showNewTeamForm, setShowNewTeamForm] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamType, setNewTeamType] = useState<TeamType>("general");
  const [isLoading, setIsLoading] = useState(true);

  const loadTeams = useCallback(async () => {
    setIsLoading(true);
    try {
      // Backend automatically filters by user's organization from profile
      const teamsData = await teamsService.getTeams();
      const teamsArray = Array.isArray(teamsData) ? teamsData : [];
      setTeams(teamsArray);
    } catch (error) {
      console.error("Error fetching teams:", error);
      toast.error("Failed to load teams");
      setTeams([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handleTeamSelection = (team: TeamData) => {
    setSelectedTeam(team.type);
    setSelectedTeamId(team.id);
    setSelectedTeamName(team.name);
  };

  const handleCreateTeam = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!newTeamName.trim()) {
      toast.error("Please enter a team name");
      return;
    }

    if (!organizationId) {
      toast.error("Please select an organization before creating a team.");
      return;
    }

    try {
      // Note: apiService returns the data directly (unwrapped from response.data.data)
      const newTeam = await teamsService.createTeam({
        name: newTeamName.trim(),
        type: newTeamType,
        description: `${newTeamType} team`,
        organizationId,
      });

      if (newTeam && newTeam.id) {
        setSelectedTeamId(newTeam.id);
        setSelectedTeamName(newTeam.name);
        setSelectedTeam(newTeam.type);
        setShowNewTeamForm(false);
        setNewTeamName("");
        setNewTeamType("general");
        toast.success(`Team "${newTeam.name}" created successfully!`);

        // Reload teams list to show the new team
        await loadTeams();
      }
    } catch (error) {
      console.error("Exception creating team:", error);
      toast.error(
        `Failed to create team: ${error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : teams.length === 0 ? (
        <div className="text-center py-8">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-semibold mb-2">No teams found</h3>
          <p className="text-muted-foreground mb-4">
            No teams have been created in your organization yet. You can create one to get started.
          </p>
          {!showNewTeamForm ? (
            <div
              className="border-2 border-dashed border-primary/30 p-6 rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
              onClick={() => setShowNewTeamForm(true)}
            >
              <div className="text-center">
                <div className="text-primary font-medium">+ Create New Team</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Start fresh with a new team for your organization
                </div>
              </div>
            </div>
          ) : (
            <div className="border-2 border-primary/30 p-6 rounded-lg bg-primary/5">
              <div className="space-y-4">
                <h4 className="font-medium text-primary">Create New Team</h4>

                <div className="space-y-2">
                  <Label htmlFor="team-name">Team Name</Label>
                  <Input
                    id="team-name"
                    placeholder="Enter team name"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="team-type">Team Type</Label>
                  <Select value={newTeamType} onValueChange={(value: TeamType) => setNewTeamType(value)}>
                    <SelectTrigger id="team-type">
                      <SelectValue placeholder="Select team type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="engineering">Engineering</SelectItem>
                      <SelectItem value="design">Design</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="sales">Sales</SelectItem>
                      <SelectItem value="hr">HR</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="operations">Operations</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleCreateTeam}
                    className="flex-1"
                  >
                    Create Team
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowNewTeamForm(false);
                      setNewTeamName("");
                      setNewTeamType("general");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground mb-4">
            Choose an existing team to join, or create a new one:
          </div>

          <RadioGroup
            value={selectedTeamId}
            onValueChange={(teamId) => {
              const selected = teams.find((team) => team.id === teamId);
              if (selected) {
                handleTeamSelection(selected);
              }
            }}
            className="space-y-3"
          >
            {teams.map((team) => (
              <div
                key={team.id}
                className="flex items-center space-x-2 border p-4 rounded-md hover:bg-muted/50 cursor-pointer"
                onClick={() => handleTeamSelection(team)}
              >
                <RadioGroupItem value={team.id} id={team.id} />
                <Label htmlFor={team.id} className="flex-1 cursor-pointer">
                  <div className="font-medium">{team.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {team.description || `${team.type} team`}
                  </div>
                </Label>
              </div>
            ))}

            {/* Option to create new team */}
            {!showNewTeamForm ? (
              <div
                className="flex items-center space-x-2 border-2 border-dashed border-primary/30 p-4 rounded-md hover:border-primary/50 hover:bg-primary/5 cursor-pointer transition-colors"
                onClick={() => setShowNewTeamForm(true)}
              >
                <RadioGroupItem value="new-team" id="new-team" />
                <Label htmlFor="new-team" className="flex-1 cursor-pointer">
                  <div className="font-medium text-primary">+ Create New Team</div>
                  <div className="text-sm text-muted-foreground">
                    Start fresh with a new team
                  </div>
                </Label>
              </div>
            ) : (
              <div className="border-2 border-primary/30 p-4 rounded-md bg-primary/5">
                <div className="space-y-4">
                  <h4 className="font-medium text-primary">Create New Team</h4>

                  <div className="space-y-2">
                    <Label htmlFor="team-name-existing">Team Name</Label>
                    <Input
                      id="team-name-existing"
                      placeholder="Enter team name"
                      value={newTeamName}
                      onChange={(e) => setNewTeamName(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="team-type-existing">Team Type</Label>
                    <Select value={newTeamType} onValueChange={(value: TeamType) => setNewTeamType(value)}>
                      <SelectTrigger id="team-type-existing">
                        <SelectValue placeholder="Select team type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="engineering">Engineering</SelectItem>
                        <SelectItem value="design">Design</SelectItem>
                        <SelectItem value="marketing">Marketing</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                        <SelectItem value="hr">HR</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                        <SelectItem value="operations">Operations</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleCreateTeam}
                      className="flex-1"
                    >
                      Create Team
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowNewTeamForm(false);
                        setNewTeamName("");
                        setNewTeamType("general");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </RadioGroup>
        </div>
      )}
    </div>
  );
};

export default TeamSelectionStep;
